<template>
  <Modal
    :isAboveAll="true"
    v-show="showUploadLogosModal"
    :allowCloseModalByClickingOutside="false"
    :hideButtonsContainer="true"
    :headerTitle="modalTitle"
    @closeModal="emitCloseModal()"
  >
    <template #content>
      <!-- Crop -->
      <div class="modal-interior-container box-shadow">
        <template v-if="loadingState || apiLoading">
          <Loader
            :withMessage="true"
            :message="'This may take a few seconds'"
          />
        </template>

        <template v-else>
          <p class="logo-name">{{ fileName }}</p>
          <!-- Crop -->
          <template v-if="modalStep === CROP_MODAL_STEP">
            <!-- Logo preview, without cropping -->
            <div
              v-if="!showCropper"
              class="image-container flex justify-center align-center"
            >
              <img style="max-height: 200px" :src="fileData" />
            </div>

            <!-- Cropper view -->
            <div v-if="showCropper" class="cropper-container">
              <!-- Cropper -->
              <vue-cropper
                ref="cropper"
                :src="fileData"
                alt="Source Image"
              ></vue-cropper>

              <!-- Cropper Buttons -->
              <div class="buttons-container">
                <button
                  class="btn white-btn mb-3 mb-md-0"
                  type="button"
                  @click="toggleCropper()"
                >
                  Cancel
                </button>
                <button
                  class="btn black-btn mb-3 mb-md-0"
                  type="button"
                  @click="cropImage()"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </template>

          <!-- Remove Background -->

          <template v-if="modalStep === REMOVE_BACKGROUND_MODAL_STEP">
            <div class="row no-gutters p-4">
              <div class="cropped-image-container col-12 col-md-6 mb-4 mb-md-0">
                <img
                  id="remove-background-img"
                  ref="remove-background-img"
                  :src="fileData"
                  style="max-width: 100%"
                  @click="setBackgroundColor($event)"
                  :class="{ editing: backgroundMode }"
                />
              </div>
              <div
                class="remove-background-button-container text-container col-12 col-md-6 flex flex-column justify-content-around align-center mb-5 mb-md-0 p-2 text-center"
              >
                <button
                  id="remove-background-btn"
                  class="btn mb-3"
                  type="button"
                  @click="startRemoveBackground()"
                >
                  <img
                    src="@/assets/icons/ui/color-wand-outline.svg"
                    alt="remove background"
                  />
                  {{ backgroundMode === 0 ? "Remove background" : "Cancel" }}
                </button>

                <p v-if="backgroundMode === 1">
                  Click the image and select the color you want to remove.
                </p>
              </div>
            </div>
          </template>

          <!-- Buttons -->
          <div class="buttons-container" v-if="!showCropper">
            <template v-if="!loadingState">
              <!-- Cancel Button -->
              <button
                class="btn cancel mb-3 mb-md-0"
                type="button"
                @click="emitCloseModal"
              >
                Cancel
              </button>

              <!-- Mid button (Back / Crop) -->
              <button
                class="btn white-btn mb-3 mb-md-0"
                type="button"
                @click="() => !loadingState && handleMidButton()"
              >
                {{ midButtonLabel }}
              </button>
              <!-- Last button (Continue/Save) -->
              <button
                class="btn black-btn mb-3 mb-md-0"
                type="button"
                @click="() => !loadingState && handleLastButton()"
              >
                {{ lastButtonLabel }}
              </button>
            </template>
            <Loader
              :withMessage="true"
              :message="'This may take a few seconds'"
              v-else
            />
          </div>
        </template>
      </div>
    </template>
  </Modal>
</template>

<script>
import Modal from "@/components/Utils/Modal.vue";
import VueCropper from "vue-cropperjs";
import axios from "axios";
import "cropperjs/dist/cropper.css";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import CONSTANTS from "@/constants";

const { CROP_MODAL_STEP, REMOVE_BACKGROUND_MODAL_STEP } = CONSTANTS.MODAL_STEPS;

import Loader from "@/components/Utils/Loader.vue";

export default {
  name: "UploadLogosModal",
  props: {
    createThreekitAsset: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    VueCropper,
    Loader,
    Modal,
  },
  data() {
    return {
      fileData: "",
      fileName: "",
      showCropper: false,
      backgroundMode: 0,
      step: 1,
      typesOfLogos: ["spiritwear", "uniforms"],
      typeOfLogoSelected: [],
      logoColors: [],
      logoHasBeenEdited: false,

      CROP_MODAL_STEP: CROP_MODAL_STEP,
      REMOVE_BACKGROUND_MODAL_STEP: REMOVE_BACKGROUND_MODAL_STEP,
    };
  },
  computed: {
    ...mapState(["auth", "dealer", "settings"]),

    ...mapState("project", ["projectLogos"]),

    ...mapState("editableLogo", ["apiLoading"]),

    ...mapState("uploadLogos", [
      "logoModalAction",
      "applyMode",
      "showUploadLogosModal",
      "modalStep",
      "loadingState",
      "editableLogo",
      "uploadedLogos",
    ]),

    ...mapGetters("uploadLogos", ["logoFileData", "normalizeLogoData"]),
    ...mapGetters("project", ["isShowroom"]),

    hasLogoFileData() {
      return !!this.logoFileData.fileName;
    },

    isLastLogo() {
      return this.uploadedLogos.length === 1;
    },

    originalLogoImage() {
      return this.editableLogo?.url;
    },

    modalTitle() {
      switch (this.modalStep) {
        case CROP_MODAL_STEP:
          return "Upload Graphic";
        case REMOVE_BACKGROUND_MODAL_STEP:
          return "Remove Background";

        default:
          return "Upload Graphic";
      }
    },

    midButtonLabel() {
      return this.modalStep === CROP_MODAL_STEP ? "Crop" : "Back";
    },

    lastButtonLabel() {
      return this.modalStep === REMOVE_BACKGROUND_MODAL_STEP
        ? "Save Graphic"
        : "Continue";
    },
  },
  methods: {
    ...mapActions("uploadLogos", [
      "goToNextLogo",
      "closeEditLogoModal",
      "setLoadingState",
      "hideEditLogoModal"
    ]),

    ...mapMutations("uploadLogos", ["setModalStep", "APPLY_MODE"]),

    emitCloseModal() {
      this.$emit("close");
      this.closeEditLogoModal();
      // !this.isManageModalOpen &&
      //   this.$emit("removeLogo", this.logoIdToBeEdited);
    },

    toggleCropper() {
      this.updateFileData();
      this.showCropper = !this.showCropper;
    },

    cropImage() {
      //mutate and save data
      this.logoHasBeenEdited = true;
      this.fileData = this.$refs.cropper.getCroppedCanvas().toDataURL();
      this.showCropper = false;
    },

    updateFileData() {
      if (!this.fileData) this.fileData = this.originalLogoImage;
    },

    startRemoveBackground() {
      if (this.backgroundMode === 0) {
        this.logoHasBeenEdited = true;
      }
      this.backgroundMode = this.backgroundMode ? 0 : 1;
    },
    setBackgroundColor(event) {
      let image = this.$refs["remove-background-img"];
      let widthRate = image.naturalWidth / image.width,
        heightRate = image.naturalHeight / image.height;

      if (this.backgroundMode === 1) {
        //Calculate the clicked coordinate
        let x = event.offsetX * widthRate,
          y = event.offsetY * heightRate;
        this.removeBackground(x, y);
      }
    },
    removeBackground(x, y) {
      if (this.fileData) {
        let image = this.$refs["remove-background-img"];
        let canvas = document.createElement("canvas");
        let ctx = canvas.getContext("2d"),
          r,
          g,
          b;

        canvas.height = image.naturalHeight;
        canvas.width = image.naturalWidth;
        ctx.drawImage(image, 0, 0);
        let pixelData = canvas.getContext("2d").getImageData(x, y, 1, 1).data;
        let imgd = ctx.getImageData(
            0,
            0,
            image.naturalWidth,
            image.naturalHeight
          ),
          pix = imgd.data,
          newColor = {
            //set transparent color
            r: 0,
            g: 0,
            b: 0,
            a: 0,
          };
        for (let i = 0, n = pix.length; i < n; i += 4) {
          r = pix[i];
          g = pix[i + 1];
          b = pix[i + 2];
          if (r == pixelData[0] && g == pixelData[1] && b == pixelData[2]) {
            //color matches the clicked one
            // Change the white to the new color.
            pix[i] = newColor.r;
            pix[i + 1] = newColor.g;
            pix[i + 2] = newColor.b;
            pix[i + 3] = newColor.a;
          }
        }
        ctx.putImageData(imgd, 0, 0);
        this.fileData = canvas.toDataURL("image/png");
        this.backgroundMode = 0;
      }
    },

    handleMidButton() {
      this.updateFileData();
      if (this.modalStep === CROP_MODAL_STEP) {
        this.toggleCropper();
      } else {
        if (this.modalStep === REMOVE_BACKGROUND_MODAL_STEP) {
          //back to Crop step
          this.setModalStep(CROP_MODAL_STEP);
        } else {
          //back to remove background step
          this.setModalStep(REMOVE_BACKGROUND_MODAL_STEP);
        }
      }
    },

    async handleLastButton() {
      try {
        this.updateFileData();

        //The modal is in the Last Step
        if (this.modalStep === REMOVE_BACKGROUND_MODAL_STEP) {
          await this.saveLogo();
          if (!this.isShowroom) {
            this.closeEditLogoModal();
          }
          else {
            this.hideEditLogoModal()
          }
        } else {
          //Else, Go to next step
          if (this.modalStep === CROP_MODAL_STEP) {
            this.setModalStep(REMOVE_BACKGROUND_MODAL_STEP);
          }
        }
      } catch (error) {
        console.log(error);
      }
    },

    async updateLogo() {
      this.setLoadingState({
        loadingState: true,
        message: "Applying Changes...",
      });
      try {
        const url =
          this.settings.endpoint + "/api/logos/" + this.editableLogo.id;

        const params = {
          type: this.typeOfLogoSelected,
          colors: this.logoColors,
        };
        //create the asset on threekit
        if (this.createThreekitAsset) params["create_asset"] = true;

        if (this.logoHasBeenEdited) params["contents"] = this.fileData;

        console.clear()
        console.log(params)

        const { data: updatedLogo } = await axios.put(url, params, {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + this.auth.apiToken,
          },
        });
        updatedLogo["content"] = this.logoHasBeenEdited
          ? this.fileData
          : this.editableLogo.content;

        console.log("updatedLogo", updatedLogo)

        return updatedLogo;
      } catch (error) {
        this.$store.dispatch("showToast", {
          txt: "Error trying to update your graphic",
          type: "error",
        });
      } finally {
        this.setLoadingState({ loadingState: false });
      }
    },

    async saveLogo() {
      try {
        this.setLoadingState({
          loadingState: true,
          message: "Applying Changes...",
        });

        const updatedLogo = await this.updateLogo();
        console.log({ updatedLogo });
        //If the project has no logos, and the user didn't choose a type, send the 2 types
        const typesOfLogo = this.typeOfLogoSelected?.length
          ? this.typeOfLogoSelected
          : this.projectLogos.length
          ? this.typeOfLogoSelected
          : ["spiritwear", "uniforms"];

        //Set the new content (the edited logo)
        this.editableLogo.content = this.fileData;
        this.editableLogo.type = typesOfLogo;
        const logo = this.normalizeLogoData(updatedLogo);

        this.$emit("saveEditedLogo", logo);
        this.$emit("saveLogo", { logo, typesOfLogo });
        this.$emit("reValidateForm");
      } catch (error) {
        this.$store.dispatch("showToast", {
          txt: "Error trying to save your graphic",
          type: "error",
        });
      } finally {
        this.setLoadingState(false);
        this.logoHasBeenEdited = false;
      }
    },

    resetModal() {
      this.setModalStep(CROP_MODAL_STEP);
      this.logoColors = this.editableLogo?.colors;
      this.fileData = this.editableLogo?.content || this.editableLogo?.url;
      this.typeOfLogoSelected = this.editableLogo?.type || [];
      this.fileName = this.editableLogo?.filename || "";
      console.log("ss");
      console.log("ss ", this.editableLogo?.content);
    },
  },

  watch: {
    editableLogo() {
      this.resetModal();
    },

    showUploadLogosModal() {
      this.showUploadLogosModal && this.resetModal();
    },
  },

  mounted() {
    this.typeOfLogoSelected = this.editableLogo?.type;
    this.logoColors = this.editableLogo.colors;

    if (this.isShowroom) {
      console.log("reset modal")
      this.resetModal()
    }
  },
};
</script>

<style lang="scss" scoped>
::v-deep .modal-container {
  max-width: 1024px !important;
  background-color: rgba(0, 0, 0, 0.3);
  .modal-interior-container {
    width: 100%;
    background-color: #ffffff;
    border-radius: 6px;
    max-width: 500px;
    min-height: 50px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 0;
    margin: auto;

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1.2rem 1.5rem;
      border-bottom: 1px solid $grey-light;
      p {
        color: $dark;
        font-size: 1.2rem;
        font-weight: 400;
        margin-left: 0.5remz;
        margin-right: auto;
        text-transform: capitalize;
      }
    }

    p.logo-name {
      margin: 0.5rem 1.25rem;
      overflow: hidden;
    }

    .btn {
      color: #000;
      font-size: 12px;
      line-height: 13px;
      padding: 8px 25px;
      border-radius: 6px;
      border: 1px solid #000;
      text-decoration: none;
      text-transform: uppercase;
      text-align: center;
      cursor: pointer;
      width: 25% !important;
      &:disabled {
        opacity: 0.25 !important;
        cursor: initial !important;
      }
    }

    #remove-background-btn {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      padding: 0.3rem;

      background: #f9fafa;
      border: 1px solid rgba(216, 218, 223, 0.2);
      box-sizing: border-box;
      border-radius: 6px;
      text-transform: capitalize;
      color: $grey-dark;
      img {
        margin-right: 0.5rem;
      }
    }

    .file-container {
      display: flex;
      align-items: center;
      margin-bottom: 20px;

      .file-extension {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 38px;
        height: 38px;
        background: linear-gradient(to bottom, #666, #000);
        border-radius: 5px;
        margin-right: 22px;
        h5 {
          color: #ffffff;
          letter-spacing: 0;
          text-transform: uppercase;
          font-weight: 400;
          font-size: 12px;
          line-height: 13px;
          margin: 0;
        }
      }
      .file-name {
        flex: 1;
        p {
          font-size: 15px;
          line-height: 17px;
          margin-bottom: 1px;
        }
        .v-progress-linear {
          ::v-deep .v-progress-linear__background.black,
          ::v-deep .v-progress-linear__determinate.black {
            background-color: #000;
            color: #000;
          }
          ::v-deep .v-progress-linear__determinate.black {
            background: url("~@/assets/logo-background.png");
            background-size: unset;
          }
        }
      }
    }
    .image-container {
      background: url("~@/assets/logo-background.png");
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 200px;
      margin: 1rem 1.25rem;
      img {
        max-width: 100%;
      }

      .loader-container {
        display: flex;
        align-items: center;
        .lds-roller {
          display: inline-block;
          position: relative;
          width: 80px;
          height: 80px;
        }
        .lds-roller div {
          animation: lds-roller 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
          transform-origin: 40px 40px;
        }
        .lds-roller div:after {
          content: " ";
          display: block;
          position: absolute;
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: rgb(17, 17, 17);
          margin: -4px 0 0 -4px;
        }
        .lds-roller div:nth-child(1) {
          animation-delay: -0.036s;
        }
        .lds-roller div:nth-child(1):after {
          top: 63px;
          left: 63px;
        }
        .lds-roller div:nth-child(2) {
          animation-delay: -0.072s;
        }
        .lds-roller div:nth-child(2):after {
          top: 68px;
          left: 56px;
        }
        .lds-roller div:nth-child(3) {
          animation-delay: -0.108s;
        }
        .lds-roller div:nth-child(3):after {
          top: 71px;
          left: 48px;
        }
        .lds-roller div:nth-child(4) {
          animation-delay: -0.144s;
        }
        .lds-roller div:nth-child(4):after {
          top: 72px;
          left: 40px;
        }
        .lds-roller div:nth-child(5) {
          animation-delay: -0.18s;
        }
        .lds-roller div:nth-child(5):after {
          top: 71px;
          left: 32px;
        }
        .lds-roller div:nth-child(6) {
          animation-delay: -0.216s;
        }
        .lds-roller div:nth-child(6):after {
          top: 68px;
          left: 24px;
        }
        .lds-roller div:nth-child(7) {
          animation-delay: -0.252s;
        }
        .lds-roller div:nth-child(7):after {
          top: 63px;
          left: 17px;
        }
        .lds-roller div:nth-child(8) {
          animation-delay: -0.288s;
        }
        .lds-roller div:nth-child(8):after {
          top: 56px;
          left: 12px;
        }
        @keyframes lds-roller {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      }
    }
    .cropper-container {
      ::v-deep .cropper-bg {
        margin-bottom: 20px;
        background: url("~@/assets/logo-background.png");
      }
    }

    .cropped-image-container {
      img {
        display: block;
        margin: auto;
        background: url("~@/assets/logo-background.png");
        border: 1px solid $grey-light;
        border-radius: 6px;
        /* max-width: 190px !important; */
        max-height: 350px;
        &.editing {
          cursor: crosshair;
        }
      }

      .text-container {
        .btn {
          margin-bottom: 16px;
        }
        h5 {
          color: red;
          margin-bottom: 9px;
        }
        p {
          color: #666;
        }
      }
    }

    .remove-background-button-container {
      margin-bottom: 2rem;

      .btn {
        width: auto !important;
      }
    }

    .step-3-container {
      display: flex;
      justify-content: space-between;
      margin-bottom: 2rem;

      .cropped-image-container {
        width: 50%;
        margin: 0;
      }

      .type-of-logo-selection-container {
        width: 45%;

        fieldset {
          border: none;
          legend {
            margin-bottom: 1rem;
          }
        }

        label {
          cursor: pointer;
        }
      }
    }

    .buttons-container {
      /* text-align: right; */
      width: 100%;
      text-align: right;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-evenly;
      padding: 1.2rem;
      button {
        width: auto !important;
      }

      .white-btn {
        border-color: #ffffff;
        margin-right: 4px;
        &:hover {
          border-color: #000000;
        }
      }
    }
  }

  .type-of-logo-container {
    width: 100%;

    fieldset {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
    }

    label {
      display: flex;
      align-items: center;
      width: 40%;
      input {
        margin-right: 0.5rem;
      }
      span {
        text-transform: capitalize;
      }
    }
  }
}
/*
 * The following styles are auto-applied to elements with
 * transition="modal" when their visibility is toggled
 * by Vue.js.
 *
 * You can easily play with the modal transition by editing
 * these styles.
 */

.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}
</style>
